'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { confirmSignUp } from '@aws-amplify/auth';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: 'Your one-time password must be 6 characters.',
  }),
});

interface Props {
  cognitoUsername: string;
  onSuccess?: () => void;
  onSuccessHref?: string;
}

export function ConfirmSignupEmailOTP({ cognitoUsername, onSuccess, onSuccessHref }: Props) {
  const router = useRouter();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: '',
    },
  });

  async function onSubmit({ pin }: z.infer<typeof FormSchema>) {
    try {
      await confirmSignUp({
        confirmationCode: pin,
        username: cognitoUsername,
      });
      
      if (onSuccess) {
        onSuccess();
      } else if (onSuccessHref) {
        router.push(onSuccessHref);
      } else {
        router.push('/login');
      }
      toast.success('Success');
    } catch (e: any) {
      form.reset();
      form.setError('pin', { message: e?.message || 'Verification failed. Please try again.' });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col items-center">
          <FormField
            control={form.control}
            name="pin"
            render={({ field }) => (
              <FormItem className="mb-4">
                <FormLabel>Enter the verification code</FormLabel>
                <FormControl>
                  <InputOTP maxLength={6} {...field}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-center">
            <Button
              className="w-sm"
              type="submit"
              disabled={form.formState.isSubmitting || !form.formState.isValid}
            >
              {form.formState.isSubmitting && (
                <Loader2 className="animate-spin mr-2" aria-hidden="true" />
              )}
              Submit
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
